import type { PaymentType, VipTypeOrder } from '@/stores/slices/commonSlice';

/** 支付弹窗三 Tab 左侧档位卡高度（设计稿 1100×760 下 100px，与 `scale` 相乘） */
export const PAYMENT_OPTION_CARD_MIN_HEIGHT_PX = 100;

/**
 * 会员升级档位卡圆角（设计稿 1100×760 基准 px，与 `scale` 相乘）。
 * 外缘半径 `OUTER`、线宽 `BORDER` 时，内缘（白底与 padding 交界处）半径 ≈ `OUTER − BORDER`（设计稿下为 3px）。
 * 角标在选中/hover 贴内角时，与内弧对齐请把 `INNER_RADIUS` 传给 PriceBadge `alignCornerRadiusPx`；贴外缘时用 `OUTER_RADIUS`。
 */
export const PAYMENT_UPGRADE_CARD_OUTER_RADIUS_PX = 8;
export const PAYMENT_UPGRADE_CARD_BORDER_PX = 3;
export const PAYMENT_UPGRADE_CARD_INNER_RADIUS_PX =
  PAYMENT_UPGRADE_CARD_OUTER_RADIUS_PX - PAYMENT_UPGRADE_CARD_BORDER_PX;

export function paymentUpgradeCardInnerRadiusPx(scale: number): number {
  return PAYMENT_UPGRADE_CARD_INNER_RADIUS_PX * scale;
}

/**
 * 左上角折扣角标在 `PriceBadge` 的 `upgradeTlMode: 'inset'`（pt 3 + 字号 12 行高 16 + pb 4）下的总高度，
 * 与选中态「升级」角标对齐（设计稿 1100×760 基准 px，乘 `scale`）。
 */
export const PAYMENT_UPGRADE_DISCOUNT_BADGE_INSET_HEIGHT_PX = 4 + 16 + 5;

/** 支付弹窗扫码区二维码边长（设计稿 1100×760 下为 106，与 `scale` 相乘） */
export const PAYMENT_DIALOG_QR_SIZE_PX = 106;

/** 支付 Tab 触发器最小宽度（设计稿 1100×760 下为 196，与 `scale` 相乘） */
export const PAYMENT_TAB_TRIGGER_MIN_WIDTH_PX = 196;

export function isMembershipUpgradeTab(type: PaymentType): boolean {
  return type === 'upgrade' || type === 'upgradePlus3x';
}

/** 支付 Tab 远程未配置时的默认三项 */
export const DEFAULT_PAYMENT_TABS: { value: PaymentType; label: string }[] = [
  { value: 'upgrade', label: 'Plus 会员' },
  { value: 'upgradePlus3x', label: 'Plus 3倍会员' },
  { value: 'recharge', label: '按需买飞豆' },
];

export function paymentTypeToVipType(type: PaymentType): VipTypeOrder | undefined {
  if (type === 'upgrade') return 'plus';
  if (type === 'upgradePlus3x') return 'plus_3x';
  return undefined;
}

/** 阶梯价 users 字段：与 LadderPrice.users 一致（依赖 vip_type，兼容历史 vip===2） */
export function ladderUserTierFromVip(
  vip: number | undefined,
  vipType?: string,
): 'normal' | 'plus' | 'plus_3x' {
  if (vip === 0 || vip === undefined || vip === null) return 'normal';
  if (vip === 2) return 'plus_3x';
  if (vip !== 1) return 'normal';
  if (vipType === 'plus_3x') return 'plus_3x';
  if (vipType === 'plus') return 'plus';
  return 'plus';
}

/** 阶梯价 users 支持 plus_3x / plus3x 两种写法 */
export function matchLadderUserTier(
  users: Array<'normal' | 'plus' | 'plus_3x' | 'plus3x'> | undefined,
  currentUserType: 'normal' | 'plus' | 'plus_3x',
): boolean {
  if (!users?.length) {
    return false;
  }
  if (users.includes(currentUserType)) {
    return true;
  }
  if (currentUserType === 'plus_3x') {
    return users.includes('plus3x');
  }
  return false;
}

/** 是否已开通会员（vip 仅为 0/1；类型见 vip_type） */
export function isPaidVip(vip: number | undefined): boolean {
  return vip === 1;
}

/** 是否为 Plus 3倍（优先 vip_type，兼容历史 vip===2） */
export function isPlus3xVip(vip: number | undefined, vipType?: string): boolean {
  if (vip === 2) return true;
  return vipType === 'plus_3x';
}
